import { useState, useEffect } from 'react';
import 'react-tabs/style/react-tabs.scss';
import './UserSettings.scss';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  getUserInfo,
  selectUserCompany,
  selectUserEmail,
  selectUserID,
  selectUserName,
  selectUserRole,
  setUserInfo,
  updateUserInfo,
} from '../../../../redux/reducers/userSlice';
import classNames from 'classnames';
import { setSettings } from '../../../../redux/reducers/popupSlice';

export default function UserSettings() {
  const dispatch = useAppDispatch();

  // Redux values
  const userEmail = useAppSelector(selectUserEmail);
  const userName = useAppSelector(selectUserName);
  const userRole = useAppSelector(selectUserRole);
  const userCompany = useAppSelector(selectUserCompany);
  const userId = useAppSelector(selectUserID);

  // Local state
  const [localUser, setLocalUser] = useState({
    customerName: userName || 'Name',
    company: userCompany || 'Company',
    role: userRole || '',
    email: userEmail,
  });

  const [errors, setErrors] = useState<{ customerName?: string; company?: string }>({});
  const [isSettingsModified, setSettingsModified] = useState(false);

  useEffect(() => {
    const isModified =
      localUser.customerName !== (userName || 'Name') ||
      localUser.role !== (userRole || '') ||
      localUser.company !== (userCompany || 'Company');
    setSettingsModified(isModified);
  }, [localUser, userName, userRole, userCompany]);

  function updateReduxUserInfo() {
    dispatch(getUserInfo(userId)).then((e) => {
      if (e?.payload?.data) {
        const dbPayload = e?.payload?.data;
        dispatch(
          setUserInfo({
            customerName: dbPayload?.name,
            ...dbPayload,
          })
        );
      }
    });
  }

  const handleResetToDefault = () => {
    setLocalUser({
      customerName: 'Name',
      company: 'Company',
      role: 'Tenant',
      email: userEmail,
    });
    setErrors({});
    setSettingsModified(true);
  };

  const handleApply = () => {
    const newErrors: typeof errors = {};

    if (!localUser.customerName.trim() || localUser.customerName === 'Name') {
      newErrors.customerName = 'Name is required';
    }
    if (!localUser.company.trim() || localUser.company === 'Company') {
      newErrors.company = 'Company is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!isSettingsModified) return;

    dispatch(updateUserInfo(localUser)).then((res) => {
      if (res?.payload?.data) {
        updateReduxUserInfo();
        setSettingsModified(false);
        setErrors({});
      }
    });
  };

  const handleCancel = () => {
    setLocalUser({
      customerName: userName || 'Name',
      company: userCompany || 'Company',
      role: userRole || '',
      email: userEmail,
    });
    setErrors({});
    setSettingsModified(false);
  };

  const handleClose = () => {
    dispatch(setSettings(false));
  };

  const handleInputChange = (field: 'customerName' | 'company', value: string) => {
    setLocalUser((prev) => ({ ...prev, [field]: value }));
    if (value.trim() !== '') {
      setErrors((prev) => ({ ...prev, [field]: undefined })); // clear error once user types
    }
  };

  const handleRoleChange = (role: string) => {
    setLocalUser((prev) => {
      let newRole = prev.role;
      if (role === 'Tenant') {
        newRole = prev.role === 'Tenant' ? '' : 'Tenant';
      } else if (role === 'Landlord') {
        newRole = prev.role === 'Landlord' ? '' : 'Landlord';
      }
      return { ...prev, role: newRole };
    });
  };

  return (
    <div className='user-settings-container'>
      <div className='user-settings-holder'>
        <div className='user-name-company'>
          <div className='user-name'>
            <label htmlFor='userName' className='user-name-label'>
              Name <span className='required-star'>*</span>
            </label>
            <input
              type='text'
              className={classNames('user-name-input', { 'input-error': errors.customerName })}
              value={localUser.customerName}
              onChange={(e) => handleInputChange('customerName', e.target.value)}
            />
            {errors.customerName && <div className='error-text'>{errors.customerName}</div>}
          </div>

          <div className='user-company'>
            <label htmlFor='usercompany' className='user-company-label'>
              Company <span className='required-star'>*</span>
            </label>
            <input
              type='text'
              className={classNames('user-company-input', { 'input-error': errors.company })}
              value={localUser.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
            />
            {errors.company && <div className='error-text'>{errors.company}</div>}
          </div>
        </div>

        <div className='user-section-header'>Login</div>
        <div className='user-login-details'>
          <div className='user-email'>
            <label htmlFor='userEmail'>Email:</label>
            <span>{userEmail}</span>
          </div>
        </div>

        <div className='user-section-header'>Default Representation</div>
        <div className='user-representation-section'>
          <div className='user-representation'>
            <input
              type='checkbox'
              name='userTenantRep'
              checked={localUser.role === 'Tenant'}
              onChange={() => handleRoleChange('Tenant')}
            />
            <span>Tenant</span>
          </div>
          <div className='user-representation'>
            <input
              type='checkbox'
              name='userLandlordRep'
              checked={localUser.role === 'Landlord'}
              onChange={() => handleRoleChange('Landlord')}
            />
            <span>Landlord</span>
          </div>
        </div>
      </div>

      <div className='bottom-container'>
        <div className='gs-reset-holder'>
          <button className='settings-button-reset' onClick={handleResetToDefault}>
            Reset
          </button>
          <div className='settings-reset-title'>User Settings</div>
        </div>

        <div className='gs-button-container'>
          {isSettingsModified && (
            <button
              disabled={!isSettingsModified}
              className={classNames('settings-button', { 'button-oranges': isSettingsModified })}
              onClick={handleApply}
            >
              Apply
            </button>
          )}
          {!isSettingsModified && (
            <button className='settings-button' onClick={handleClose}>
              Close
            </button>
          )}
          {isSettingsModified && (
            <button className='settings-button' onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
